import { Button, Icon, Radio, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  DetailItem,
  GiftMessageArea,
  ReviewMessageBadge,
  ToggleMenu
} from '@hero/components/molecules';
import { CardIsleWidget } from '@hero/components/widgets';
import { configs, constants } from '@hero/configs';
import { CARDISLE_GREETING_CARD, CONTACT_TYPE, GIFT_MESSAGE_TYPE } from '@hero/constants';
import { getAddonData, getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

function GiftMessage({
  setShowMessageLibrary,
  setExperienceSettings,
  experienceSettings,
  associationData,
  updateAssociation,
  isDetailItemEditable,
  isSuccessAssociation,
  isOpenCustomizeMessageModal,
  setOpenCustomizeMessageModal,
  footerProps,
  customMessageLength,
  isReviewMessageBadgeShown
}) {
  const { t } = useTranslation();
  const { recognitionCode, code } = useParams();

  const [activeAccordion, setActiveAccordion] = useState('');
  const [selectedGreetingCard, setSelectedGreetingCard] = useState(null);

  useEffect(() => {
    const greetingCard = getAddonData(associationData?.addons, CARDISLE_GREETING_CARD);
    if (greetingCard) {
      setActiveAccordion(GIFT_MESSAGE_TYPE.cardMessage);
      setSelectedGreetingCard(greetingCard);
    } else {
      setActiveAccordion(GIFT_MESSAGE_TYPE.inputMessage);
      setSelectedGreetingCard(null);
    }
  }, [associationData]);

  const MessageLibraryLabel = (
    <Button
      variant="link"
      onClick={() => {
        setShowMessageLibrary(true);
      }}
    >
      <Icon name="preview" />
      {t('library')}
    </Button>
  );

  const toggleMenuTooltipContent = recognitionCode
    ? 'giftMessageOptionRecognitionDisabledOptionMessage'
    : 'giftMessageOptionAutomationDisabledOptionMessage';

  const InputComponent = () => {
    const addonList = associationData?.addons?.filter(
      addon => addon.type !== CARDISLE_GREETING_CARD
    );

    return (
      <DetailItem
        withBorder={false}
        editable={isDetailItemEditable}
        closeWithoutSave={() => {
          setExperienceSettings({
            ...experienceSettings,
            messageParams: {
              ...experienceSettings.messageParams,
              message: associationData?.experienceSettings?.messageParams?.message
            }
          });
        }}
        saveDetail={() => {
          updateAssociation({
            params: { recognitionCode, code },
            body: {
              experienceSettings,
              addons: addonList
            }
          });
        }}
        className="text-editor"
        bodyStyle={{ border: 'none' }}
        titleStyle={{ display: 'none' }}
        editInput={() =>
          isSuccessAssociation && (
            <GiftMessageArea
              textAreaProps={{
                placeholder: t('giftMessage'),
                value: experienceSettings?.messageParams?.message,
                limit: constants?.general?.giftMessageLimit,
                onChange: e => {
                  if (configs?.experience?.customGiftMessageEditorEnabled) {
                    const wordArray = e.split('\n');
                    wordArray.length = 3;

                    setExperienceSettings({
                      ...experienceSettings,
                      messageParams: {
                        ...experienceSettings.messageParams,
                        message: e
                      }
                    });
                  } else {
                    setExperienceSettings({
                      ...experienceSettings,
                      messageParams: {
                        ...experienceSettings.messageParams,
                        message: e
                      }
                    });
                  }
                },
                tags: associationData?.contacts?.type === CONTACT_TYPE.list && [
                  {
                    name: t('recipientSName', { name: t('firstName') }),
                    placeholder: '{{firstName}}'
                  },
                  {
                    name: t('recipientSName', { name: t('lastName') }),
                    placeholder: '{{lastName}}'
                  },
                  {
                    name: t('recipientSName', { name: `${t('firstName')}&${t('lastName')}` }),
                    placeholder: '{{firstName}} {{lastName}}'
                  }
                ],
                RightContent: MessageLibraryLabel
              }}
              footerProps={{
                ...footerProps,
                isOpenCustomizeMessageModal,
                setOpenCustomizeMessageModal,
                customMessageLength,
                isIconTitlesHide: true
              }}
            />
          )
        }
      >
        <S.OverviewWrapper>
          <Typography
            as="div"
            className="text-editor-preview"
            variant="body1"
            {...getSafeInnerHtml(associationData?.experienceSettings?.messageParams?.message)}
          />
          {isReviewMessageBadgeShown && (
            <ReviewMessageBadge
              customMessageLength={customMessageLength}
              setOpenCustomizeMessageModal={setOpenCustomizeMessageModal}
            />
          )}
        </S.OverviewWrapper>
      </DetailItem>
    );
  };

  return (
    <DetailItem
      title={`${t('giftMessage')}:`}
      withBorder
      closeWithoutSave={() => {
        setExperienceSettings({
          ...experienceSettings,
          messageParams: {
            ...experienceSettings.messageParams,
            message: associationData?.experienceSettings?.messageParams?.message
          }
        });
      }}
      saveDetail={() => {
        updateAssociation({
          params: { recognitionCode, code },
          body: {
            experienceSettings
          }
        });
      }}
      actionAreaStyle={{
        top: '205px',
        bottom: 'auto'
      }}
    >
      {configs?.experience?.greetingCardEnabled ? (
        <S.MessageExperienceWrapper>
          <ToggleMenu
            isActive={activeAccordion === GIFT_MESSAGE_TYPE.inputMessage}
            onClick={() => {
              setActiveAccordion(GIFT_MESSAGE_TYPE.inputMessage);
            }}
            title={
              <S.ToggleMenuTitleWrapper>
                <Radio
                  id={GIFT_MESSAGE_TYPE.inputMessage}
                  checked={activeAccordion === GIFT_MESSAGE_TYPE.inputMessage}
                  label={
                    <Typography variant="body2">
                      {t('standardGiftMessageExperienceTitle')}
                    </Typography>
                  }
                  readOnly
                />
              </S.ToggleMenuTitleWrapper>
            }
            hasAccordionIcon={false}
            disabled={!footerProps.editable ? selectedGreetingCard : false}
            tooltipProps={{
              content:
                !footerProps.editable && selectedGreetingCard ? t(toggleMenuTooltipContent) : ''
            }}
            style={{
              backgroundColor: 'var(--color-bg-weaker)'
            }}
          >
            {InputComponent()}
          </ToggleMenu>
          <ToggleMenu
            isActive={activeAccordion === GIFT_MESSAGE_TYPE.cardMessage}
            onClick={() => {
              setActiveAccordion(GIFT_MESSAGE_TYPE.cardMessage);
            }}
            title={
              <S.ToggleMenuTitleWrapper>
                <Radio
                  id={GIFT_MESSAGE_TYPE.cardMessage}
                  checked={activeAccordion === GIFT_MESSAGE_TYPE.cardMessage}
                  label={t('greetingCardExperienceTitle')}
                  readOnly
                />
              </S.ToggleMenuTitleWrapper>
            }
            hasAccordionIcon={false}
            disabled={!footerProps.editable && !selectedGreetingCard}
            tooltipProps={{
              content:
                !footerProps.editable && !selectedGreetingCard ? t(toggleMenuTooltipContent) : ''
            }}
            style={{
              backgroundColor: 'var(--color-bg-weaker)'
            }}
          >
            {associationData && activeAccordion === GIFT_MESSAGE_TYPE.cardMessage && (
              <CardIsleWidget
                page="experienceReview"
                selectedCard={selectedGreetingCard}
                updateCard={data => {
                  const addons = associationData?.addons?.filter(
                    addon => addon.type !== CARDISLE_GREETING_CARD
                  );
                  setExperienceSettings({
                    ...experienceSettings,
                    messageParams: {
                      ...experienceSettings.messageParams,
                      message: ''
                    }
                  });
                  updateAssociation({
                    params: { recognitionCode, code },
                    body: {
                      experienceSettings: {
                        ...experienceSettings,
                        messageParams: { ...experienceSettings.messageParams, message: '' }
                      },
                      addons: [
                        ...addons,
                        {
                          type: CARDISLE_GREETING_CARD,
                          data
                        }
                      ]
                    }
                  });
                }}
              />
            )}
          </ToggleMenu>
        </S.MessageExperienceWrapper>
      ) : (
        InputComponent()
      )}
    </DetailItem>
  );
}

GiftMessage.propTypes = {
  setShowMessageLibrary: PropTypes.func,
  setExperienceSettings: PropTypes.func,
  experienceSettings: PropTypes.shape({
    messageParams: PropTypes.shape({ message: PropTypes.string })
  }),
  associationData: PropTypes.shape({
    experienceSettings: PropTypes.shape({
      messageParams: PropTypes.shape({ message: PropTypes.string })
    }),
    contacts: PropTypes.shape({ type: PropTypes.string }),
    addons: PropTypes.array
  }),
  updateAssociation: PropTypes.func,
  isDetailItemEditable: PropTypes.bool,
  isSuccessAssociation: PropTypes.bool,
  isOpenCustomizeMessageModal: PropTypes.bool,
  setOpenCustomizeMessageModal: PropTypes.func,
  footerProps: PropTypes.object,
  customMessageLength: PropTypes.number,
  isReviewMessageBadgeShown: PropTypes.bool
};

export { GiftMessage };
